/**
 * The game plan's "Email the coaches at your schools" picker (APS-259).
 * Pure helpers: which saved schools can be emailed, what is picked first,
 * what a send costs in free programs, and who gets the email.
 */

export interface StaffMember {
  production_id?: number | string | null;
  coach_name?: string | null;
  title?: string | null;
  position?: string | null;
  email?: string | null;
  has_email?: boolean;
}

export interface SavedSchool {
  school_id: number;
  name: string;
  logo_url?: string | null;
  category?: 'dream' | 'target' | 'safety';
  coaching_staff?: StaffMember[] | null;
}

export interface ProgramRow {
  school_id: number;
  name: string;
  logo_url: string | null;
  /** Coaches on file with an email address (redacted or not). */
  emailCount: number;
  /** The staff as the lists API sent it. Emails show for paid and held programs. */
  staff?: StaffMember[];
}

/**
 * The two house schools every new athlete gets on their list: Softball Media
 * on X (999994) and APS University (999996). Mirrors backend utils/houseSchools.js.
 */
export const HOUSE_SCHOOL_IDS: readonly number[] = [999994, 999996];

/** A paid family starts with this many schools picked. */
export const PAID_PRESELECT = 10;

/** Above this many coaches the picker warns about Gmail's daily limit. */
export const BIG_SEND = 100;

export interface UnlockState {
  isPaid: boolean;
  remaining: number;
  /** Programs this athlete already holds, as strings. */
  unlocked: Set<string>;
}

export interface Recipient {
  coach_name: string;
  email: string;
  title?: string;
  school?: string;
  schoolLogo?: string;
  coach_id?: string;
  school_id?: string;
}

const CATEGORY_ORDER: Record<string, number> = { dream: 0, target: 1, safety: 2 };

function hasEmail(c: StaffMember | null | undefined): boolean {
  if (!c) return false;
  return !!(c.email && c.email.trim()) || c.has_email === true;
}

/**
 * The saved schools, 3 hearts first, each school once. `rows` can be emailed;
 * `noEmail` counts the schools with no coach email on file.
 */
export function programRows(schools: SavedSchool[]): { rows: ProgramRow[]; noEmail: number } {
  const seen = new Set<number>();
  const ordered = schools
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      const ca = CATEGORY_ORDER[a.s.category || ''] ?? 3;
      const cb = CATEGORY_ORDER[b.s.category || ''] ?? 3;
      return ca - cb || a.i - b.i;
    })
    .map(({ s }) => s);
  const rows: ProgramRow[] = [];
  let noEmail = 0;
  for (const s of ordered) {
    if (!s || seen.has(s.school_id)) continue;
    seen.add(s.school_id);
    if (HOUSE_SCHOOL_IDS.includes(Number(s.school_id))) continue;
    const emailCount = (s.coaching_staff || []).filter(hasEmail).length;
    if (emailCount === 0) {
      noEmail += 1;
      continue;
    }
    rows.push({
      school_id: s.school_id,
      name: s.name,
      logo_url: s.logo_url || null,
      emailCount,
      staff: s.coaching_staff || [],
    });
  }
  return { rows, noEmail };
}

/**
 * The picker's unlock state from GET /coach-unlocks/status, or null when the
 * response is missing or unreadable. The picker does not guess.
 */
export function unlockStateFrom(status: {
  isPaid?: boolean;
  remaining?: number;
  schoolIds?: string[];
} | null | undefined): UnlockState | null {
  if (!status) return null;
  if (status.isPaid === true) return { isPaid: true, remaining: 0, unlocked: new Set() };
  if (status.isPaid !== false || typeof status.remaining !== 'number') return null;
  return {
    isPaid: false,
    remaining: Math.max(0, status.remaining),
    unlocked: new Set((status.schoolIds || []).map(String)),
  };
}

/** True when emailing this program spends a free program. */
export function costsProgram(schoolId: number, state: UnlockState): boolean {
  return !state.isPaid && !state.unlocked.has(String(schoolId));
}

/**
 * What is picked when the picker opens: the first PAID_PRESELECT schools for a
 * paid family. A free family gets the programs it already holds plus new ones
 * up to what is left.
 */
export function initialSelection(rows: ProgramRow[], state: UnlockState): Set<number> {
  if (state.isPaid) return new Set(rows.slice(0, PAID_PRESELECT).map((r) => r.school_id));
  const picked = new Set<number>();
  let budget = Math.max(0, state.remaining);
  for (const r of rows) {
    if (!costsProgram(r.school_id, state)) picked.add(r.school_id);
    else if (budget > 0) {
      picked.add(r.school_id);
      budget -= 1;
    }
  }
  return picked;
}

/** The picked schools that spend a free program. */
export function newPrograms(selected: Set<number>, rows: ProgramRow[], state: UnlockState): ProgramRow[] {
  return rows.filter((r) => selected.has(r.school_id) && costsProgram(r.school_id, state));
}

/** True when one more new program can be picked without a plan. */
export function canPickAnother(selected: Set<number>, rows: ProgramRow[], state: UnlockState): boolean {
  if (state.isPaid) return true;
  return newPrograms(selected, rows, state).length < Math.max(0, state.remaining);
}

/**
 * The picks that still fit the free programs left: every held program, then
 * new ones in list order up to what is left.
 */
export function fitSelection(selected: Set<number>, rows: ProgramRow[], state: UnlockState): Set<number> {
  if (state.isPaid) return new Set(selected);
  const out = new Set<number>();
  let budget = Math.max(0, state.remaining);
  for (const r of rows) {
    if (!selected.has(r.school_id)) continue;
    if (!costsProgram(r.school_id, state)) out.add(r.school_id);
    else if (budget > 0) {
      out.add(r.school_id);
      budget -= 1;
    }
  }
  return out;
}

/**
 * Recipients straight from the lists response, for a program that costs
 * nothing. Null when any coach with an email on file came back redacted, so
 * the caller asks the server for the staff instead.
 */
export function listedRecipients(row: ProgramRow, state: UnlockState): Recipient[] | null {
  if (costsProgram(row.school_id, state)) return null;
  const staff = row.staff || [];
  const hidden = staff.some((c) => c && c.has_email === true && !(c.email && c.email.trim()));
  if (hidden) return null;
  const out = recipientsFor(row, staff);
  return out.length > 0 ? out : null;
}

/** Coaches the composer can show, from the staff an unlock sent back. */
export function recipientsFor(school: ProgramRow, staff: StaffMember[] | null | undefined): Recipient[] {
  const out: Recipient[] = [];
  for (const c of staff || []) {
    const email = (c && c.email ? c.email : '').trim();
    if (!email) continue;
    out.push({
      coach_name: (c.coach_name || '').trim() || 'Coach',
      email,
      title: c.title || c.position || undefined,
      school: school.name,
      schoolLogo: school.logo_url || undefined,
      coach_id: c.production_id != null ? String(c.production_id) : undefined,
      school_id: String(school.school_id),
    });
  }
  return out;
}

/** One entry per email address, first one kept. */
export function dedupeRecipients(list: Recipient[]): Recipient[] {
  const seen = new Set<string>();
  return list.filter((r) => {
    const key = r.email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** "Auburn", "Auburn and Duke", "Auburn, Duke and LSU". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The confirm before free programs are spent. */
export function confirmCopy(fresh: ProgramRow[], remaining: number): { title: string; message: string } {
  const n = fresh.length;
  const left = Math.max(0, remaining - n);
  const title = n === 1 ? 'Use 1 free program?' : `Use ${n} free programs?`;
  const leftLine = left === 0 ? "You'll have none left." : `You'll have ${left} left.`;
  return {
    title,
    message: `This unlocks ${joinNames(fresh.map((r) => r.name))}. Every coach there stays unlocked. ${leftLine}`,
  };
}
